import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Star, Minus, Plus, Timer, RefreshCw, Play, Pause, X, Send, Lock, Crown, ChevronDown } from 'lucide-react'
import { useAuth } from '../hooks/useAuth.jsx'
import { useTimers, formatTime } from '../hooks/useTimers.jsx'
import { paper, leather, fonts, shadows } from '../styles/theme.js'
import { formatAmount, getConversions, parseDuration } from '../utils/conversions.js'

export default function RecipeDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { authFetch, isSubscriber } = useAuth()
  const { addTimer, getRecipeTimers, timers, toggleTimer, resetTimer, removeTimer, addCustomTimer } = useTimers()

  const [recipe, setRecipe] = useState(null)
  const [loading, setLoading] = useState(true)
  const [servings, setServings] = useState(1)
  const [baseServings, setBaseServings] = useState(1)
  const [favorite, setFavorite] = useState(false)
  const [notes, setNotes] = useState('')
  const [editingNotes, setEditingNotes] = useState(false)
  const [conversionPopup, setConversionPopup] = useState(null) // {ingredient, x, y}
  const [chatMessages, setChatMessages] = useState([])
  const [chatInput, setChatInput] = useState('')
  const [chatLoading, setChatLoading] = useState(false)
  const [customTimerOpen, setCustomTimerOpen] = useState(false)
  const [customTimerLabel, setCustomTimerLabel] = useState('')
  const [customTimerMins, setCustomTimerMins] = useState(5)
  const chatEndRef = useRef(null)

  const recipeTimers = getRecipeTimers(Number(id))

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`/api/recipes/${id}`)
      if (res.ok) {
        const d = await res.json()
        setRecipe(d)
        setBaseServings(d.servings)
        setServings(d.servings)
        setFavorite(d.favorite)
        setNotes(d.notes || '')
      }
    } catch (e) { console.error(e) }
    finally { setLoading(false) }
  }, [authFetch, id])

  useEffect(() => { load() }, [load])

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [chatMessages])

  const scaleAmount = (amount) => {
    if (!amount || !baseServings) return amount
    return (Number(amount) * servings) / baseServings
  }

  const toggleFav = async () => {
    setFavorite(f => !f)
    try { await authFetch(`/api/recipes/${id}/favorite`, { method: 'PATCH' }) }
    catch { setFavorite(f => !f) }
  }

  const saveNotes = async () => {
    try {
      await authFetch(`/api/recipes/${id}/notes`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ notes }),
      })
      setEditingNotes(false)
    } catch (e) { console.error(e) }
  }

  const handleConvert = (ing, e) => {
    e.stopPropagation()
    const rect = e.currentTarget.getBoundingClientRect()
    setConversionPopup({ ingredient: ing, x: rect.left, y: rect.bottom + 8 })
  }

  const handleTimerClick = (step) => {
    const secs = parseDuration(step.timer)
    if (secs) addTimer(step.timer, secs, Number(id), recipe?.title)
  }

  const sendChat = async () => {
    if (!chatInput.trim() || chatLoading) return
    const msg = chatInput.trim()
    setChatInput('')
    setChatMessages(prev => [...prev, { role: 'user', content: msg }])
    setChatLoading(true)
    try {
      const res = await authFetch('/api/ai/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ recipeId: Number(id), message: msg, history: chatMessages }),
      })
      if (res.ok) {
        const d = await res.json()
        setChatMessages(prev => [...prev, { role: 'assistant', content: d.content }])
      } else {
        const e = await res.json()
        setChatMessages(prev => [...prev, { role: 'assistant', content: `Error: ${e.error || 'Something went wrong'}` }])
      }
    } catch {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'Connection error. Please try again.' }])
    } finally { setChatLoading(false) }
  }

  if (loading) return <LoadingView />
  if (!recipe) return <NotFoundView onBack={() => navigate('/recipes')} />

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: paper.bg }}>
      {/* Main scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', paddingBottom: 60 }}
        onClick={() => setConversionPopup(null)}>

        {/* Sticky header */}
        <div style={{
          position: 'sticky', top: 0, zIndex: 20,
          background: `${paper.bgDark}f0`, backdropFilter: 'blur(8px)',
          borderBottom: `1px solid ${paper.borderMed}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 20px',
        }}>
          <button onClick={() => navigate('/recipes')} style={iconBtnStyle()}>
            <ArrowLeft size={16} />
            <span style={{ fontFamily: fonts.serif }}>Contents</span>
          </button>
          <button onClick={toggleFav} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Star size={22} fill={favorite ? leather.gold : 'none'} color={favorite ? leather.gold : paper.borderMed} />
          </button>
        </div>

        <div style={{ maxWidth: 700, margin: '0 auto', padding: '24px 20px' }}>
          {/* Title card */}
          <div style={{ marginBottom: 28, paddingBottom: 20, borderBottom: `1px solid ${paper.borderMed}` }}>
            <h1 style={{ fontFamily: fonts.cursive, fontSize: 38, color: paper.ink, marginBottom: 8 }}>
              {recipe.title}
            </h1>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
              {(recipe.tags || []).map(tag => (
                <span key={tag} style={tagStyle()}>{tag}</span>
              ))}
            </div>
            {/* Servings scaler */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontFamily: fonts.serif, fontSize: 16, color: paper.inkLight }}>Servings:</span>
              <button onClick={() => setServings(s => Math.max(1, s - 1))} style={scalerBtn()}>
                <Minus size={14} />
              </button>
              <span style={{ fontFamily: fonts.serif, fontSize: 20, color: paper.ink, minWidth: 28, textAlign: 'center', fontWeight: 600 }}>
                {servings}
              </span>
              <button onClick={() => setServings(s => s + 1)} style={scalerBtn()}>
                <Plus size={14} />
              </button>
            </div>
          </div>

          {/* Ingredients */}
          <section style={{ marginBottom: 28 }}>
            <h2 style={sectionHead()}>Ingredients</h2>
            <ul style={{ listStyle: 'none', padding: 0 }}>
              {(recipe.ingredients || []).map((ing, i) => {
                const scaled = scaleAmount(ing.amount)
                return (
                  <li key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 0', borderBottom: `1px solid ${paper.borderMed}40`,
                  }}>
                    <span style={{ fontFamily: fonts.serif, fontSize: 17, color: paper.ink, flex: 1 }}>
                      <strong>{formatAmount(scaled)} {ing.unit}</strong> {ing.item}
                    </span>
                    {ing.unit && (
                      <button onClick={(e) => handleConvert(ing, e)} style={{
                        fontSize: 12, fontFamily: fonts.serif, padding: '2px 8px',
                        background: paper.bgDark, border: `1px solid ${paper.borderMed}`,
                        borderRadius: 10, cursor: 'pointer', color: paper.inkLight,
                        whiteSpace: 'nowrap',
                      }}>
                        Convert
                      </button>
                    )}
                  </li>
                )
              })}
            </ul>
          </section>

          {/* Instructions */}
          <section style={{ marginBottom: 28 }}>
            <h2 style={sectionHead()}>Instructions</h2>
            <ol style={{ listStyle: 'none', padding: 0 }}>
              {(recipe.steps || []).map((step, i) => {
                const duration = step.timer ? parseDuration(step.timer) : null
                return (
                  <li key={i} style={{
                    display: 'flex', gap: 14, marginBottom: 16,
                    padding: '14px 16px', background: paper.bgWhite,
                    borderRadius: 8, border: `1px solid ${paper.borderMed}50`,
                    boxShadow: shadows.card,
                  }}>
                    <span style={{
                      width: 28, height: 28, background: paper.ink, color: paper.bgWhite,
                      borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontFamily: fonts.serif, fontSize: 14, fontWeight: 600, flexShrink: 0,
                    }}>{i + 1}</span>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontFamily: fonts.serif, fontSize: 17, color: paper.ink, lineHeight: 1.5, marginBottom: duration ? 8 : 0 }}>
                        {step.text}
                      </p>
                      {duration && (
                        <button onClick={() => handleTimerClick(step)} style={{
                          display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                          background: paper.accentWarm, border: 'none', borderRadius: 12,
                          color: 'white', cursor: 'pointer', fontFamily: fonts.mono, fontSize: 13,
                        }}>
                          <Timer size={12} />
                          {step.timer}
                        </button>
                      )}
                    </div>
                  </li>
                )
              })}
            </ol>
          </section>

          {/* Notes */}
          <section>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <h2 style={{ ...sectionHead(), marginBottom: 0 }}>Notes</h2>
              {isSubscriber && !editingNotes && (
                <button onClick={() => setEditingNotes(true)} style={iconBtnStyle()}>
                  Edit
                </button>
              )}
            </div>
            {editingNotes ? (
              <div>
                <textarea
                  value={notes} onChange={e => setNotes(e.target.value)}
                  rows={5} style={{
                    width: '100%', padding: 12, fontFamily: fonts.serif, fontSize: 16,
                    border: `1px solid ${paper.borderMed}`, borderRadius: 6,
                    background: paper.bgWhite, color: paper.ink, resize: 'vertical', outline: 'none',
                  }}
                  placeholder="Add personal notes, substitutions, tips…"
                />
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <button onClick={saveNotes} style={{ ...iconBtnStyle(), background: paper.ink, color: 'white', border: 'none' }}>Save</button>
                  <button onClick={() => setEditingNotes(false)} style={iconBtnStyle()}>Cancel</button>
                </div>
              </div>
            ) : (
              <p style={{ fontFamily: fonts.serif, fontSize: 16, color: notes ? paper.inkLight : paper.inkFaint, lineHeight: 1.6, fontStyle: notes ? 'normal' : 'italic' }}>
                {notes || 'No notes yet. Subscribe to add personal notes.'}
              </p>
            )}
          </section>
        </div>
      </div>

      {/* Fixed Sidebar */}
      <div style={{
        width: 370, flexShrink: 0, borderLeft: `1px solid ${paper.borderMed}`,
        background: paper.bgWhite, display: 'flex', flexDirection: 'column',
        boxShadow: shadows.sidebar, position: 'sticky', top: 0, height: '100vh', overflowY: 'auto',
      }}>
        {/* Timers Panel */}
        <div style={{ padding: 16, borderBottom: `1px solid ${paper.borderMed}` }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <h3 style={{ fontFamily: fonts.serif, fontSize: 18, color: paper.ink, fontWeight: 600 }}>
              ⏱ Timers
            </h3>
            <button onClick={() => setCustomTimerOpen(o => !o)} style={iconBtnStyle()}>
              <Plus size={14} /> Add
            </button>
          </div>

          {customTimerOpen && (
            <div style={{ marginBottom: 12, padding: 10, background: paper.bgDark, borderRadius: 6, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <input
                value={customTimerLabel} onChange={e => setCustomTimerLabel(e.target.value)}
                placeholder="Timer label" style={{ padding: '6px 10px', fontFamily: fonts.serif, fontSize: 14, border: `1px solid ${paper.borderMed}`, borderRadius: 4, outline: 'none' }}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <label style={{ fontFamily: fonts.serif, fontSize: 14, color: paper.inkLight }}>Minutes:</label>
                <input type="number" value={customTimerMins} onChange={e => setCustomTimerMins(Number(e.target.value))} min={1} max={600}
                  style={{ width: 60, padding: '4px 8px', fontFamily: fonts.mono, fontSize: 14, border: `1px solid ${paper.borderMed}`, borderRadius: 4, outline: 'none' }}
                />
              </div>
              <button onClick={() => {
                addCustomTimer(customTimerLabel || `${customTimerMins}m timer`, customTimerMins * 60)
                setCustomTimerOpen(false); setCustomTimerLabel(''); setCustomTimerMins(5)
              }} style={{ ...iconBtnStyle(), background: paper.ink, color: 'white', border: 'none', justifyContent: 'center' }}>
                Start Timer
              </button>
            </div>
          )}

          {recipeTimers.length === 0 && (
            <p style={{ fontFamily: fonts.serif, fontSize: 14, color: paper.inkFaint, fontStyle: 'italic' }}>
              Tap a step timer to start it
            </p>
          )}

          {recipeTimers.map(t => (
            <TimerCard key={t.id} timer={t} onToggle={toggleTimer} onReset={resetTimer} onRemove={removeTimer} />
          ))}
        </div>

        {/* Chef's Oracle (AI) */}
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ padding: '12px 16px', borderBottom: `1px solid ${paper.borderMed}` }}>
            <h3 style={{ fontFamily: fonts.serif, fontSize: 18, color: paper.ink, fontWeight: 600 }}>
              🔮 Chef's Oracle
            </h3>
            <p style={{ fontFamily: fonts.serif, fontSize: 13, color: paper.inkFaint, marginTop: 2 }}>
              Ask anything about this recipe
            </p>
          </div>

          {!isSubscriber ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24, textAlign: 'center' }}>
              <Crown size={40} color={leather.gold} style={{ marginBottom: 12 }} />
              <p style={{ fontFamily: fonts.serif, fontSize: 17, color: paper.ink, marginBottom: 6 }}>
                Chef's Oracle is a subscriber feature
              </p>
              <p style={{ fontFamily: fonts.serif, fontSize: 14, color: paper.inkFaint, marginBottom: 16 }}>
                Ask Claude for substitutions, tips, and pairings
              </p>
              <Lock size={20} color={paper.inkFaint} />
            </div>
          ) : (
            <>
              <div style={{ flex: 1, overflowY: 'auto', padding: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {chatMessages.length === 0 && (
                  <div style={{ fontFamily: fonts.serif, fontSize: 14, color: paper.inkFaint, fontStyle: 'italic', textAlign: 'center', marginTop: 20 }}>
                    Ask me about substitutions, techniques, pairings…
                  </div>
                )}
                {chatMessages.map((msg, i) => (
                  <div key={i} style={{
                    alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                    maxWidth: '85%', padding: '8px 12px', borderRadius: 10,
                    background: msg.role === 'user' ? paper.ink : paper.bgDark,
                    color: msg.role === 'user' ? paper.bgWhite : paper.ink,
                    fontFamily: fonts.serif, fontSize: 15, lineHeight: 1.5,
                  }}>
                    {msg.content}
                  </div>
                ))}
                {chatLoading && (
                  <div style={{ fontFamily: fonts.mono, fontSize: 13, color: paper.inkFaint, alignSelf: 'flex-start' }}>
                    Consulting the cookbook…
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
              <div style={{ padding: 12, borderTop: `1px solid ${paper.borderMed}`, display: 'flex', gap: 8 }}>
                <input
                  value={chatInput}
                  onChange={e => setChatInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendChat()}
                  placeholder="Ask the Oracle…"
                  style={{
                    flex: 1, padding: '8px 12px', fontFamily: fonts.serif, fontSize: 15,
                    border: `1px solid ${paper.borderMed}`, borderRadius: 8,
                    outline: 'none', background: paper.bgWhite, color: paper.ink,
                  }}
                />
                <button onClick={sendChat} disabled={chatLoading || !chatInput.trim()} style={{
                  padding: '8px 12px', background: paper.ink, border: 'none', borderRadius: 8,
                  cursor: 'pointer', color: 'white', opacity: chatLoading || !chatInput.trim() ? 0.5 : 1,
                }}>
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Conversion Popup */}
      {conversionPopup && (
        <ConversionPopup
          ingredient={conversionPopup.ingredient}
          scaledAmount={scaleAmount(conversionPopup.ingredient.amount)}
          x={conversionPopup.x}
          y={conversionPopup.y}
          onClose={() => setConversionPopup(null)}
        />
      )}
    </div>
  )
}

function TimerCard({ timer, onToggle, onReset, onRemove }) {
  const pct = timer.total > 0 ? (timer.remaining / timer.total) * 100 : 0
  return (
    <div style={{
      marginBottom: 8, padding: 10, background: timer.done ? `${paper.accentWarm}15` : paper.bgDark,
      borderRadius: 8, border: `1px solid ${timer.done ? paper.accentWarm : paper.borderMed}`,
      animation: timer.done ? 'pulse 0.8s ease-in-out infinite' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontFamily: fonts.serif, fontSize: 14, color: timer.done ? paper.accentWarm : paper.ink, fontWeight: timer.done ? 700 : 400 }}>
          {timer.done ? '✅ ' : ''}{timer.label}
        </span>
        <button onClick={() => onRemove(timer.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}>
          <X size={12} color={paper.inkFaint} />
        </button>
      </div>
      <div style={{ fontFamily: fonts.mono, fontSize: 28, color: timer.done ? paper.accentWarm : paper.ink, textAlign: 'center', marginBottom: 6 }}>
        {timer.done ? 'Done!' : formatTime(timer.remaining)}
      </div>
      {/* Progress bar */}
      <div style={{ height: 3, background: paper.borderMed, borderRadius: 2, marginBottom: 8 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: timer.done ? paper.accentWarm : paper.ink, borderRadius: 2, transition: 'width 1s linear' }} />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button onClick={() => onToggle(timer.id)} style={iconBtnStyle()}>
          {timer.running ? <Pause size={14} /> : <Play size={14} />}
          {timer.running ? 'Pause' : 'Resume'}
        </button>
        <button onClick={() => onReset(timer.id)} style={iconBtnStyle()}>
          <RefreshCw size={14} /> Reset
        </button>
      </div>
    </div>
  )
}

function ConversionPopup({ ingredient, scaledAmount, x, y, onClose }) {
  const conversions = getConversions(scaledAmount, ingredient.unit, ingredient.type)
  return (
    <div
      onClick={e => e.stopPropagation()}
      style={{
        position: 'fixed', left: Math.min(x, window.innerWidth - 260), top: y,
        width: 240, background: paper.bgWhite, border: `1px solid ${paper.borderMed}`,
        borderRadius: 8, boxShadow: shadows.popup, zIndex: 100, padding: 12,
        animation: 'fadeIn 0.15s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <span style={{ fontFamily: fonts.serif, fontSize: 14, fontWeight: 600, color: paper.ink }}>
          {formatAmount(scaledAmount)} {ingredient.unit} {ingredient.item}
        </span>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
          <X size={14} color={paper.inkFaint} />
        </button>
      </div>
      {conversions.length === 0 ? (
        <p style={{ fontFamily: fonts.serif, fontSize: 13, color: paper.inkFaint }}>No conversions available</p>
      ) : (
        conversions.map(c => (
          <div key={c.unit} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: `1px solid ${paper.borderMed}30` }}>
            <span style={{ fontFamily: fonts.serif, fontSize: 14, color: paper.inkLight }}>{c.label}</span>
            <span style={{ fontFamily: fonts.mono, fontSize: 14, color: paper.ink, fontWeight: 600 }}>{c.value}</span>
          </div>
        ))
      )}
    </div>
  )
}

function sectionHead() {
  return { fontFamily: fonts.cursive, fontSize: 24, color: paper.ink, marginBottom: 12, paddingBottom: 6, borderBottom: `1px solid ${paper.borderMed}` }
}
function tagStyle() {
  return { padding: '2px 10px', background: paper.bgDark, border: `1px solid ${paper.borderMed}`, borderRadius: 12, fontFamily: fonts.serif, fontSize: 13, color: paper.inkLight }
}
function scalerBtn() {
  return { width: 30, height: 30, border: `1px solid ${paper.borderMed}`, borderRadius: 6, background: paper.bgDark, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }
}
function iconBtnStyle() {
  return { display: 'flex', alignItems: 'center', gap: 4, padding: '6px 10px', background: 'none', border: `1px solid ${paper.borderMed}`, borderRadius: 6, cursor: 'pointer', fontFamily: fonts.serif, fontSize: 13, color: paper.inkLight }
}

function LoadingView() {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: paper.bg }}>
      <p style={{ fontFamily: fonts.cursive, fontSize: 24, color: paper.inkFaint }}>Loading recipe…</p>
    </div>
  )
}
function NotFoundView({ onBack }) {
  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: paper.bg, gap: 16 }}>
      <p style={{ fontFamily: fonts.cursive, fontSize: 24, color: paper.ink }}>Recipe not found</p>
      <button onClick={onBack} style={iconBtnStyle()}><ArrowLeft size={16} /> Back to Contents</button>
    </div>
  )
}
